import type { InputTextProperties } from "@jetblue/kit/input-text";
import {
  ComboBox,
  ListBox,
  ListLayout,
  Popover,
  Virtualizer,
} from "react-aria-components";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import { InputText } from "@jetblue/kit/input-text";

import type { Airport as AirportType } from "@jb-dotcom/api/odb/transformed";
import { Airport } from "./airport";
import { Region } from "./region";
import { AirportIndex } from "@jetblue/fuzzy-search";
import type { UseQueryResult } from "@tanstack/react-query";

export type InputCityProperties = {
  error?: InputTextProperties["error"];
  inputValue: string | undefined;
  isRequired?: boolean | undefined;
  label: string;
  name?: string | undefined;
  odData: NonNullable<UseQueryResult<AirportType[]>>["data"];
  onInputChange: (value: string) => void;
  showValidation?: boolean | undefined;
};
export const useFilteredItems = (
  inputValue: string,
  items: InputCityProperties["odData"] = [],
) => {
  const deferredInputValue = useDeferredValue(inputValue);
  const indexReference = useRef<AirportIndex>(null);
  useEffect(() => {
    indexReference.current ??= new AirportIndex();
    indexReference.current.set_items(items);
  }, [items]);

  return useMemo(() => {
    const searchString = deferredInputValue.toLowerCase();
    return (
      searchString && indexReference.current
        ? indexReference.current
            .search(searchString)
            .map((match) => match.candidate)
        : items
    ).flatMap(
      (airport) =>
        [
          airport,
          ...(airport.cc ?? []).map((a) => ({ ...a, indent: true })),
        ] as (typeof airport & { indent?: boolean })[],
    );
  }, [items, deferredInputValue]);
};
export const InputCity = ({
  error,
  inputValue,
  isRequired,
  label,
  name,
  odData = [],
  onInputChange,
  showValidation = false,
}: InputCityProperties) => {
  const [textBoxInputValue, setTextBoxInputValue] = useState("");
  const deferredValue = useDeferredValue(textBoxInputValue);

  const isInvalid = showValidation && !inputValue;

  const filteredData = useFilteredItems(textBoxInputValue, odData);

  return (
    <ComboBox
      aria-label={label}
      className="flex-1"
      isInvalid={isInvalid}
      isRequired={isRequired}
      items={filteredData}
      menuTrigger="focus"
      name={name}
      onInputChange={setTextBoxInputValue}
      onSelectionChange={(value) => {
        onInputChange(value?.toString() ?? "");
      }}
      selectedKey={inputValue}
    >
      <InputText isInvalid={isInvalid} label={label} selectOnFocus />
      {error}
      <Popover
        className="w-[--trigger-width] min-w-[368px] rounded-b-md border border-medium-gray bg-white"
        crossOffset={0}
        offset={0}
        placement="bottom start"
      >
        <Virtualizer layout={ListLayout}>
          <ListBox
            className="max-h-48 overflow-scroll md:max-h-80"
            dependencies={[deferredValue]}
          >
            {({ cc, code, needle, name }: (typeof filteredData)[number]) =>
              cc ? (
                <Region
                  cc={cc}
                  code={code}
                  inputValue={deferredValue}
                  name={name}
                  needle={needle}
                />
              ) : (
                <Airport
                  code={code}
                  inputValue={deferredValue}
                  name={name}
                  needle={needle}
                />
              )
            }
          </ListBox>
        </Virtualizer>
      </Popover>
    </ComboBox>
  );
};
